// threeSetup.js
// Configuración profesional de escena Three.js
// Dependencias: three, OrbitControls

import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

/**
 * Inicializa una escena 3D completa con luces, cámara, controles y helpers.
 * @param {React.MutableRefObject} mountRef - referencia al div contenedor.
 * @param {Object} [options] - opciones personalizables.
 * @param {number} [options.height=480] - altura del canvas.
 * @param {boolean} [options.enableGrid=true] - muestra la cuadrícula base.
 * @param {boolean} [options.enableAxes=true] - muestra los ejes XYZ.
 * @param {boolean} [options.enableStats=false] - muestra estadísticas FPS (si está disponible).
 * @param {THREE.Color | number | string} [options.background=0xf0f0f0] - color de fondo.
 * @returns {{ scene: THREE.Scene, camera: THREE.PerspectiveCamera, renderer: THREE.WebGLRenderer, controls: OrbitControls, resizeHandler: Function }}
 */
export function initThreeScene(mountRef, options = {}) {
  const {
    height = 480,
    enableGrid = true,
    enableAxes = true,
    enableStats = false,
    background = 0xf0f0f0,
  } = options;

  if (!mountRef?.current) {
    console.error("initThreeScene: mountRef inválido o nulo.");
    return null;
  }

  // --- Medidas base ---
  const width = mountRef.current.clientWidth || 800;

  // --- Escena ---
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(background);

  // --- Cámara ---
  const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 5000);
  camera.position.set(150, 150, 150);
  camera.lookAt(0, 0, 0);

  // --- Renderizador ---
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    powerPreference: "high-performance",
  });
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setSize(width, height);
  renderer.outputEncoding = THREE.sRGBEncoding;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  mountRef.current.appendChild(renderer.domElement);

  // --- Iluminación ---
  const ambient = new THREE.AmbientLight(0x888888, 1.0);
  scene.add(ambient);

  const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
  dirLight.position.set(100, 150, 100);
  dirLight.castShadow = true;
  dirLight.shadow.camera.near = 1;
  dirLight.shadow.camera.far = 1000;
  dirLight.shadow.mapSize.set(2048, 2048);
  scene.add(dirLight);

  // --- Helpers visuales ---
  if (enableGrid) {
    const grid = new THREE.GridHelper(400, 80, 0xcccccc, 0xeeeeee);
    grid.material.opacity = 0.6;
    grid.material.transparent = true;
    scene.add(grid);
  }

  if (enableAxes) {
    const axes = new THREE.AxesHelper(80);
    scene.add(axes);
  }

  // --- Controles de cámara ---
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.rotateSpeed = 0.6;
  controls.zoomSpeed = 0.8;
  controls.panSpeed = 0.5;
  controls.minDistance = 20;
  controls.maxDistance = 1000;
  controls.target.set(0, 0, 0);
  controls.update();

  // --- Animación (loop principal) ---
  const animate = () => {
    requestAnimationFrame(animate);
    controls.update();
    renderer.render(scene, camera);
  };
  animate();

  // --- Resize automático ---
  const resizeHandler = () => {
    if (!mountRef.current) return;
    const newWidth = mountRef.current.clientWidth;
    camera.aspect = newWidth / height;
    camera.updateProjectionMatrix();
    renderer.setSize(newWidth, height);
  };

  window.addEventListener("resize", resizeHandler);

  // --- FPS Stats opcional ---
  let stats = null;
  if (enableStats) {
    import("three/examples/jsm/libs/stats.module.js").then(({ default: Stats }) => {
      stats = new Stats();
      stats.dom.style.position = "absolute";
      stats.dom.style.top = "5px";
      mountRef.current.appendChild(stats.dom);

      const updateStats = () => {
        stats.update();
        requestAnimationFrame(updateStats);
      };
      updateStats();
    });
  }

  // --- Limpieza ---
  const cleanup = () => {
    window.removeEventListener("resize", resizeHandler);
    controls.dispose();
    renderer.dispose();
    if (mountRef.current?.contains(renderer.domElement)) {
      mountRef.current.removeChild(renderer.domElement);
    }
    scene.clear();
  };

  return {
    scene,
    camera,
    renderer,
    controls,
    resizeHandler,
    cleanup,
  };
}
